import { useLocation, useNavigate } from 'react-router-dom';
import { motion, LayoutGroup } from 'framer-motion';
import { cn } from '@/utils/cn';
import {
  isLibraryAreaPath,
  isProfileAreaPath,
  isLeagueAreaPath,
  isDashboardAreaPath,
} from './bottomNavigationUtils';
import homeIcon from '@/assets/bottom-nav/home.png';
import tipsIcon from '@/assets/bottom-nav/tips.png';
import bookIcon from '@/assets/bottom-nav/book.png';
import leagueIcon from '@/assets/bottom-nav/league.png';
import profileIcon from '@/assets/bottom-nav/profile.png';

interface BottomNavigationProps {
  className?: string;
}

const BottomNavigation = ({ className }: BottomNavigationProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  if (location.pathname === '/tips') {
    return null;
  }

  const navTabs = [
    { title: 'خانه', icon: homeIcon, path: '/dashboard' },
    { title: 'نکات', icon: tipsIcon, path: '/tips', isSpecial: true },
    { title: 'کتابخانه', icon: bookIcon, path: '/library' },
    { title: 'لیگ', icon: leagueIcon, path: '/league' },
    { title: 'پروفایل', icon: profileIcon, path: '/profile' },
  ];

  const handleNavigation = (path: string) => {
    if (path === '/library') {
      // The library tab must always open the library. A previous learning
      // path is unrelated to explicit bottom-tab navigation.
      localStorage.removeItem('lastLearningPath');
      localStorage.removeItem('lastLearningPathBookId');
    }

    navigate(path, { replace: path === '/library' });
  };

  const getActiveIndex = () => {
    if (isProfileAreaPath(location.pathname)) {
      return 4;
    }

    if (isLeagueAreaPath(location.pathname)) {
      return 3;
    }

    if (isLibraryAreaPath(location.pathname)) {
      return 2;
    }

    if (isDashboardAreaPath(location.pathname)) {
      return 0;
    }

    return 0;
  };

  const activeIndex = getActiveIndex();

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50',
        '[-webkit-tap-highlight-color:transparent]',
        className
      )}
      dir="rtl"
      style={{
        paddingTop: 0,
      }}
    >
      <div
        className={cn(
          'relative w-full',
          'bg-white dark:bg-dark-bg-secondary',
          'border-t border-gray-200/50 dark:border-dark-border/50',
          'px-4 py-1.5'
        )}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <LayoutGroup id="bottom-nav">
          <div className="relative flex w-full items-center justify-between gap-2">
            {navTabs.map((tab, index) => {
              const isActive = index === activeIndex;

              return (
                <button
                  type="button"
                  key={tab.path}
                  onClick={() => handleNavigation(tab.path)}
                  aria-label={tab.title}
                  className={cn(
                    'relative z-10 flex items-center justify-center',
                    'h-11 w-11 rounded-xl p-0',
                    'transition-all duration-300 ease-out',
                    'focus:outline-none focus:ring-0',
                    'outline-none ring-0 border-0',
                    'appearance-none select-none bg-transparent',
                    'active:outline-none active:ring-0 focus-visible:outline-none',
                    '[-webkit-tap-highlight-color:transparent]',
                    '[touch-action:manipulation]',
                    isActive ? 'scale-105' : 'hover:scale-105 active:scale-95'
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {isActive && (
                    <motion.div
                      layoutId="bottomNavIndicator"
                      className="absolute inset-0 flex items-center justify-center pointer-events-none z-0"
                      transition={{
                        type: 'spring',
                        stiffness: 350,
                        damping: 30,
                        mass: 0.8,
                      }}
                    >
                      <div
                        className={cn(
                          'h-full w-full rounded-xl',
                          'border-2 border-[#6e61ff]/80',
                          'bg-transparent'
                        )}
                        style={{ willChange: 'transform, opacity' }}
                      />
                    </motion.div>
                  )}

                    <img
                      src={tab.icon}
                      alt=""
                      aria-hidden="true"
                      draggable="false"
                      className={cn(
                      'relative z-10 h-8 w-8 shrink-0 object-contain',
                      'transition-all duration-300',
                      isActive ? 'scale-105' : 'scale-100'
                    )}
                  />
                </button>
              );
            })}
          </div>
        </LayoutGroup>
      </div>
    </nav>
  );
};

export default BottomNavigation;
